import { FC, useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  CircularProgress,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";

import { ReactComponent as NoUpdatesIcon } from "./../../../assets/Icons/no-updates.svg";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import imagePlaceholader from "./../../../assets/image-placeholader.png";
import { GRANTVESTMENT_STATUS, GrantVestment } from "types/grant-vestment";
import { getPercentage, moneyFormat } from "utils/money-format";
import { longFormat, numberOfDays, shortFormat } from "utils/date-format";
import { useNavigate } from "react-router-dom";
import { USER_ROLE } from "types/account";
import { USER_STATUS } from "types/fund-account-invitation";
import { AuthContext } from "providers/userProvider";
import { AuthData } from "types/auth";
import GrantVestmentInteractions from "./grant-vestment-actions";
import {
  useDeleteGrantVestmentFollowerMutation,
  useGetGrantVestmentFollowerQuery,
} from "hooks/use-services/grant-vestment.service";

interface GrantVestmentsDetailsCardProps {
  grantvestment: GrantVestment;
  isAuthenticated: boolean;
  buttonLabel?: string;
  hideProgressBar?: boolean;
  handleOpen: (value: boolean) => void;
}

const GrantVestmentsDetailsCard: FC<GrantVestmentsDetailsCardProps> = ({
  grantvestment,
  isAuthenticated,
  buttonLabel,
  hideProgressBar,
  handleOpen,
}) => {
  const navigate = useNavigate();
  const { userData, userRole } = useContext<AuthData>(AuthContext);
  const [followState, setFollowState] = useState<boolean | null>(null);

  const {
    data: followerData,
    isSuccess: followerSuccess,
    isLoading: followerIsLoading,
  } = useGetGrantVestmentFollowerQuery({
    filter: {
      grantvestmentId: grantvestment.grantVestmentId!,
      userId: userData?.user?.userId ?? 0,
    },
  });

  useEffect(() => {
    if (followerSuccess && followerData?.pages[0].data.length > 0) {
      setFollowState(followerData?.pages[0].data.length > 0 ? true : false);
    }
  }, [followerSuccess, followerData]);

  const deleteGrantvestmentFollower = useDeleteGrantVestmentFollowerMutation();
  const funded = getPercentage(
    grantvestment?.raisedAmount,
    grantvestment?.goalAmount
  );
  const daysLeft =
    numberOfDays(grantvestment?.deadline) < 0
      ? 0
      : numberOfDays(grantvestment?.deadline);

  const grantVest = () => {
    if (isAuthenticated) {
      navigate(`/grant-vestments/${grantvestment?.grantVestmentId}/request`);
    } else {
      handleOpen(false);
    }
  };

  return (
    <>
      <Card
        className="single"
        sx={{
          backgroundColor: "transparent",
          mt: 5,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          width: "100%",
          maxWidth: "100%",
          overflow: "hidden",
        }}
      >
        <CardMedia
          component="img"
          sx={{
            width: { xs: "100%", md: "33%" },
            height: { xs: "250px", md: "auto" },
            minHeight: { md: "300px" },
            objectFit: "contain",
            objectPosition: "center",
            "&:hover": { cursor: "pointer" },
            flex: { xs: "none", md: "0 0 33%" },
          }}
          image={
            grantvestment.imageUrl ? grantvestment.imageUrl : imagePlaceholader
          }
          alt="Grant vestment project image"
        />

        {grantvestment?.status === GRANTVESTMENT_STATUS.CLOSED ||
        grantvestment?.status === GRANTVESTMENT_STATUS.EXITED ? (
          <Box
            sx={{
              ml: { xs: 0, md: 2 },
              mt: { xs: 2, md: 4 },
              mb: { xs: 2, md: 4 },
              width: "100%",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              p: { xs: 2, md: 0 },
            }}
          >
            <CardContent sx={{ flex: 1, pb: 1 }}>
              <Typography
                variant="h3"
                sx={{
                  color: "neutral.main",
                  fontSize: { xs: "1.5rem", md: "2rem" },
                }}
                gutterBottom
              >
                {grantvestment?.grantVestorCounter} GrantVestorsᵀᴹ backed this
                project with ${moneyFormat(grantvestment?.raisedAmount)}
              </Typography>
              {!hideProgressBar && (
                <Box sx={{ width: "100%", my: 2 }}>
                  <LinearProgress
                    color="primary"
                    variant="determinate"
                    value={100}
                  />
                </Box>
              )}

              <Typography
                variant="smallCopy"
                color={"primary"}
                className="title"
                paragraph
              >
                {funded}% funded
              </Typography>

              <Typography
                variant="bodyCopyBold"
                sx={{ color: "neutral.main" }}
                paragraph
              >
                Funds raised between {shortFormat(grantvestment?.launchedAt)}{" "}
                and {shortFormat(grantvestment?.closedAt)}
              </Typography>
            </CardContent>
            <CardActions disableSpacing sx={{ mt: "auto", px: 2 }}>
              {followerIsLoading && (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <CircularProgress color="primary" size={20} />
                </Box>
              )}

              <Button
                startIcon={<NoUpdatesIcon />}
                variant="outlined"
                color="primary"
                size="large"
                fullWidth={true}
                onClick={() => {
                  if (
                    (followerData?.pages?.[0]?.data ?? []).length > 0 &&
                    followerSuccess
                  ) {
                    deleteGrantvestmentFollower.mutate(
                      followerData?.pages?.[0]?.data?.[0]
                        ?.grantVestmentFollowerId
                    );
                    setFollowState(false);
                  }
                }}
                disabled={
                  deleteGrantvestmentFollower.isLoading ||
                  followerIsLoading ||
                  !followerSuccess ||
                  (followerSuccess &&
                    followerData?.pages?.[0]?.data?.length === 0) ||
                  !followState
                }
              >
                Stop receiving updates
              </Button>
            </CardActions>
          </Box>
        ) : grantvestment?.status === GRANTVESTMENT_STATUS.RAISING ? (
          <Box
            sx={{
              ml: { xs: 0, md: 2 },
              width: "100%",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              p: { xs: 2, md: 0 },
            }}
          >
            <CardContent sx={{ flex: 1, pb: 1 }}>
              {isAuthenticated ? (
                <>
                  <Typography
                    variant="smallCopy"
                    className="title"
                    sx={{ color: "neutral.lighter" }}
                    paragraph
                  >
                    Raised
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      color: "neutral.main",
                      fontSize: { xs: "1.5rem", md: "2rem" },
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                    gutterBottom
                  >
                    ${moneyFormat(grantvestment?.raisedAmount!)}
                    <Typography
                      variant="subtitle3"
                      sx={{
                        display: { xs: "block", md: "block" },
                        fontSize: { xs: "0.9rem", md: "1rem" },
                      }}
                    >
                      ${moneyFormat(grantvestment?.goalAmount!)} goal
                    </Typography>
                  </Typography>
                  <Box sx={{ width: "100%", my: 2 }}>
                    <LinearProgress
                      color="primary"
                      variant="determinate"
                      value={parseFloat(funded) > 100 ? 100 : parseInt(funded)}
                    />
                  </Box>
                  <Typography
                    variant="smallCopy"
                    color={"primary"}
                    className="title"
                    paragraph
                  >
                    {funded}% funded
                  </Typography>
                </>
              ) : (
                <>
                  <Typography
                    variant="subtitle3"
                    sx={{ color: "neutral.main" }}
                    paragraph
                  >
                    Amount to be raised
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      color: "neutral.main",
                      fontSize: { xs: "1.5rem", md: "2rem" },
                    }}
                    gutterBottom
                  >
                    ${moneyFormat(grantvestment?.goalAmount!)}
                  </Typography>
                </>
              )}

              <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
                <AccessTimeIcon />
                <Typography
                  variant="bodyCopyBold"
                  sx={{
                    color: "neutral.main",
                    fontSize: { xs: "0.9rem", md: "1rem" },
                  }}
                >
                  {daysLeft} days to go - until{" "}
                  {shortFormat(grantvestment?.deadline)}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
                <PeopleOutlineIcon />
                <Typography
                  variant="bodyCopyBold"
                  sx={{
                    color: "neutral.main",
                    fontSize: { xs: "0.9rem", md: "1rem" },
                  }}
                >
                  {grantvestment?.grantVestorCounter}
                </Typography>
              </Stack>
              {grantvestment?.allOrNothing && (
                <>
                  <Typography
                    variant="smallCopyBold"
                    sx={{ color: "neutral.lighter", fontWeight: 700 }}
                  >
                    All or nothing
                  </Typography>
                  <Typography
                    variant="smallCopy"
                    sx={{ color: "neutral.lighter" }}
                    paragraph
                  >
                    This project only be funded if it reaches its goal by{" "}
                    {longFormat(grantvestment?.deadline)}
                  </Typography>
                </>
              )}
            </CardContent>
            {isAuthenticated && (
              <CardActions disableSpacing sx={{ mt: "auto", px: 2 }}>
                <Grid container spacing={2} alignItems={"center"}>
                  <Grid
                    item
                    xs={12}
                    sm={7}
                    md={7}
                    sx={{ textAlign: { xs: "center", sm: "start" } }}
                  >
                    <Button
                      disabled={
                        daysLeft === 0 ||
                        userRole === USER_ROLE.VIEWER ||
                        userData?.user?.status === USER_STATUS.PENDING
                      }
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth={true}
                      onClick={grantVest}
                    >
                      {buttonLabel ? buttonLabel : "Recommend a GrantVestment"}
                    </Button>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={5}
                    md={5}
                    sx={{ textAlign: { xs: "center", sm: "end" } }}
                  >
                    {userData?.user && grantvestment && (
                      <GrantVestmentInteractions
                        grantvestment={grantvestment}
                        user={userData.user}
                      />
                    )}
                  </Grid>
                </Grid>
              </CardActions>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              ml: { xs: 0, md: 2 },
              width: "100%",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              p: { xs: 2, md: 0 },
            }}
          >
            <CardContent sx={{ flex: 1, pb: 1 }}>
              <>
                <Typography
                  variant="subtitle3"
                  className="title"
                  sx={{ color: "neutral.lighter" }}
                  paragraph
                >
                  Coming Soon
                </Typography>

                <Typography
                  variant="bodyCopyRegular"
                  sx={{ color: "neutral.lighter" }}
                  paragraph
                >
                  This GrantVestment is coming soon! Stay updated by hitting
                  "Follow" to receive all the latest updates.
                </Typography>
              </>
            </CardContent>
            {isAuthenticated && (
              <CardActions disableSpacing sx={{ mt: "auto", px: 2 }}>
                <Grid container spacing={2} alignItems={"center"}>
                  <Grid
                    item
                    xs={12}
                    sm={7}
                    md={7}
                    sx={{ textAlign: { xs: "center", sm: "start" } }}
                  ></Grid>
                  <Grid
                    item
                    xs={12}
                    sm={5}
                    md={5}
                    sx={{ textAlign: { xs: "center", sm: "end" } }}
                  >
                    {userData?.user && grantvestment && (
                      <GrantVestmentInteractions
                        grantvestment={grantvestment}
                        user={userData.user}
                      />
                    )}
                  </Grid>
                </Grid>
              </CardActions>
            )}
          </Box>
        )}
      </Card>
    </>
  );
};

export default GrantVestmentsDetailsCard;
